/**
 * automationTiers.ts — the capability-model vocabulary for the automation
 * builder (docs/AUTOMATION-CAPABILITY-MODEL.md).
 *
 * Pure module, no React and no I/O, so every rule in it is unit-testable:
 * which tiers a permission set may create, how a tier-scoped rule type is
 * namespaced (and read back), and the one honest sentence for a suspended
 * automation. The server is the authority on all of it — everything here
 * mirrors a landed server rule and names it.
 */
import { rules } from '@cwos/api-client';

export type AuthorityTier = rules.AuthorityTier;

/**
 * The tier → permission map, exactly as `POST /api/rules` charges it
 * (apps/api/src/domains/rules/routes.ts, TIER_CREATE_PERMISSION) and as the
 * service re-derives it inside the tenant transaction. The organization tier
 * keeps its existing key — nothing about today's org rules changes.
 */
export const TIER_CREATE_PERMISSION: Readonly<Record<AuthorityTier, string>> = Object.freeze({
  personal: 'automation:manage_own',
  team: 'automation:manage_team',
  organization: 'rules:configure',
});

/** Display order: the narrowest authority first. */
const TIER_ORDER: readonly AuthorityTier[] = Object.freeze(['personal', 'team', 'organization']);

/**
 * The tiers THIS caller may create, in display order. A tier whose key the
 * caller does not hold is ABSENT — never grayed out — because offering it
 * would promise a create the server will 403.
 */
export function availableTiers(has: (key: string) => boolean): AuthorityTier[] {
  return TIER_ORDER.filter((tier) => has(TIER_CREATE_PERMISSION[tier]));
}

/**
 * The stored rule type for a tier-scoped automation.
 *
 * `workflow_rule_type_org_uk` predates tiers and is deliberately untouched, so
 * rule types stay unique per org and a personal/team instantiation of a
 * catalog type must be namespaced per owner (the capability model's
 * `personal.reminder.{ownerUserId}` pattern). The builder does this
 * invisibly: the person sees the catalog entry's friendly name; this string is
 * what the row stores. Organization tier keeps the bare catalog type — that
 * uniqueness (one org rule per type) is the existing contract.
 */
export function namespacedRuleType(
  tier: AuthorityTier,
  baseType: string,
  ownerUserId: string,
): string {
  if (tier === 'organization') return baseType;
  return `${tier}.${baseType}.${ownerUserId}`;
}

/**
 * Two suffix forms namespace a tier-scoped type, and this parse must read
 * BOTH — it is a mirror of the server's own, which is the authority:
 * `TIER_NAMESPACED_RULE_TYPE` in apps/api/src/domains/rules/schemas.ts.
 *
 *   - the manual builder's per-owner UUID   (`namespacedRuleType`, below)
 *   - Intelligence's per-plan 32-hex digest (`automationRuleTypeOf`,
 *     apps/api/src/domains/intelligence/automation/durableStore.ts)
 *
 * F392: this copy accepted only the first, so every Intelligence-created
 * automation fell through to its raw namespaced type and RulesSettings found
 * no catalog entry behind it — no friendly name, no description, no trigger
 * category. The two regexes cannot yet share one definition: the server never
 * imports client code and `apps/api` does not depend on `@cwos/api-client`.
 * Both DO depend on `@cwos/shared`, which is the place to converge them —
 * see this lane's report. Until then, changing either one means changing both.
 */
const NAMESPACED_TYPE =
  /^(personal|team)\.(.+)\.((?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|(?:[0-9a-f]{32}))$/;

/**
 * The catalog type behind a stored rule type — the inverse of
 * `namespacedRuleType`, for surfacing the friendly catalog entry of a
 * tier-scoped rule. A type that does not match the namespace shape is its own
 * base (every organization rule, and any future shape this parser has not been
 * taught — falling through to the raw type is honest, never wrong).
 */
export function baseRuleType(ruleType: string): string {
  const m = NAMESPACED_TYPE.exec(ruleType);
  return m ? m[2]! : ruleType;
}

/**
 * Drop the parameters a PERSONAL automation is forbidden to carry.
 *
 * The server REFUSES (never strips) an audience parameter on a personal
 * automation — model rule 1. The builder therefore never offers or sends one:
 * dropping a catalog DEFAULT the person was never shown is not silent
 * stripping of their intent, it is declining to pre-fill a field the server
 * would refuse. The returned `removed` list is what lets the UI say so out
 * loud when it happens.
 */
export function withoutAudienceParams(params: Record<string, unknown>): {
  params: Record<string, unknown>;
  removed: string[];
} {
  const out: Record<string, unknown> = {};
  const removed: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (rules.PERSONAL_AUDIENCE_PARAMETER_KEYS.includes(key)) {
      removed.push(key);
    } else {
      out[key] = value;
    }
  }
  return { params: out, removed };
}

/**
 * The one honest sentence for `suspended_reason = 'suspended_authority'`
 * (model rule 2): the executor re-checked the owner's PRESENT authority at
 * fire time and found it no longer covers the audience. Rendered wherever the
 * rule's state is rendered — never hidden, never softened into "paused".
 */
export const SUSPENDED_AUTHORITY_TEXT =
  'Paused: the owner no longer has authority over this audience. It will resume on its own if that authority comes back.';

/** Plain-language tier names for pills and the builder's cards. */
export const TIER_LABEL: Readonly<Record<AuthorityTier, string>> = Object.freeze({
  personal: 'Personal',
  team: 'Team',
  organization: 'Organization',
});
